import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import clsx from 'clsx';
import { CustomLoader } from 'components/CustomLoader/CustomLoader';
import InactivityTimer from 'components/InactivityTimer/InactivityTimer';
import store from 'store';
import { productsApi } from 'store/slices/products-api';
import { profileApi } from 'store/slices/profile-api';
import { setTheme } from 'store/slices/theme-slice';
import { preloadQueries } from 'utils/preloading';
import { getSignupStatus } from 'utils/signup-status';
import {
  decodeToken,
  getTokenStatus,
  loadTokenFromStorage,
  setToken,
} from 'utils/token';
import { SideNav } from '../../components/SideNav/SideNav';
import { featureFlagsApi } from 'store/slices/feature-flags-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AiInsights } from '../../components/AiInsights/AiInsights';
import { AiInsightsContext } from '../../components/AiInsights/aiInsights-context';
import {
  isTermsDismissedForSession,
  setTermsDismissedForSession,
  useTermsVisibility,
} from './trade/components/TermsModal/useTermsVisibility';
import TermsModal from './trade/components/TermsModal/TermsModal';
export const Route = createFileRoute('/_authenticated')({
  component: PrivateLayout,
  beforeLoad: async ({ location }) => {
    store.dispatch(setTheme('dark'));
    const token = loadTokenFromStorage();
    const tokenStatus = getTokenStatus(decodeToken(token));

    if (tokenStatus.status == 'authenticated') {
      setToken(token!);
    } else if (tokenStatus.status == 'unauthenticated') {
      throw redirect({
        to: '/auth/login',
        search: {
          redirect: location.href,
        },
      });
    } else if (tokenStatus.status == 'needs-2fa') {
      setToken(token!);
      throw redirect({
        to: '/auth/login/2fa',
        search: {
          redirect: location.href,
        },
      });
    }

    const { active, defaultSignupPage } = await getSignupStatus();
    if (!active) {
      throw redirect({
        to: defaultSignupPage!,
      });
    }
  },
  pendingComponent: () => <CustomLoader page />,
  loader: async () => {
    await preloadQueries(
      profileApi.endpoints.getProfile.initiate(),
      profileApi.endpoints.getLoginMethods.initiate(),
      productsApi.endpoints.getAvailableProducts.initiate(),
      featureFlagsApi.endpoints.getFeatureFlags.initiate(),
      profileApi.endpoints.getAccessToSegment.initiate('BETA_AI_FEATURES'),
    );
  },
});

export default function PrivateLayout() {
  const pendingTermsCount = useTermsVisibility();
  const [showTermsPopup, setShowTermsPopup] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerQuestion, setDrawerQuestion] = useState<string>('');
  const [pageName, setPageName] = useState<'Portfolio' | 'Assets' | 'Explore'>(
    'Portfolio',
  );
  const [productCode, setProductCode] = useState<string | undefined>(undefined);
  const [productType, setProductType] = useState<string | undefined>(undefined);
  const openAiInsights = useCallback(
    (
      question: string,
      pageName: 'Portfolio' | 'Assets' | 'Explore',
      productCode?: string,
      productType?: string,
    ) => {
      setDrawerQuestion(question);
      setPageName(pageName);
      setDrawerOpen(true);
      setProductCode(productCode);
      setProductType(productType);
    },
    [],
  );

  const closeAiInsights = useCallback(() => {
    setDrawerOpen(false);
    setDrawerQuestion('');
    setProductCode(undefined);
    setProductType(undefined);
  }, []);

  const drawerContextValue = useMemo(
    () => ({
      isOpen: drawerOpen,
      question: drawerQuestion,
      pageName,
      productCode,
      productType,
      openAiInsights,
      closeAiInsights,
    }),
    [
      drawerOpen,
      drawerQuestion,
      openAiInsights,
      closeAiInsights,
      pageName,
      productCode,
      productType,
    ],
  );

  useEffect(() => {
    if (pendingTermsCount > 0 && !isTermsDismissedForSession()) {
      setShowTermsPopup(true);
    }
  }, [pendingTermsCount]);

  return (
    <AiInsightsContext.Provider value={drawerContextValue}>
      <div className="relative min-h-screen">
        <InactivityTimer />
        <div
          className={clsx(
            'transition-[margin-right] duration-300 ease-in-out',
            drawerOpen ? 'mr-80' : 'mr-0',
          )}>
          <SideNav>
            <Outlet />
          </SideNav>
          {showTermsPopup && (
            <TermsModal
              isOpen={showTermsPopup}
              handleClose={() => {
                setShowTermsPopup(false);
                setTermsDismissedForSession(true);
              }}
            />
          )}
        </div>
        <AiInsights
          isOpen={drawerOpen}
          onClose={closeAiInsights}
          question={drawerQuestion}
          pageName={pageName}
          productCode={productCode}
          productType={productType}
        />
      </div>
    </AiInsightsContext.Provider>
  );
}
